<template>
    <Button label="Add Transaction" icon="pi pi-plus" severity="info" @click="toggleModal" class="p-button-sm" />

    <Dialog v-model:visible="visible" modal header="Add Transaction" :style="{ width: '450px' }" :closeOnEscape="false">
        <div class="flex flex-col gap-4">
            <div class="field">
                <label for="amount" class="block text-sm font-medium mb-2">Amount</label>
                <div class="flex gap-2">
                    <div class="flex-none">
                        <Button icon="pi pi-plus" severity="success" @click="isPositive = true"
                            :class="{ 'p-button-outlined': !isPositive }" class="p-button-sm" />
                        <Button icon="pi pi-minus" severity="danger" @click="isPositive = false"
                            :class="{ 'p-button-outlined': isPositive }" class="p-button-sm ml-2" />
                    </div>
                    <InputNumber v-model="amount" id="amount" mode="currency" currency="EUR" :min="0"
                        :minFractionDigits="0" :maxFractionDigits="2" class="w-full" @input="handleAmountChange" />
                </div>
            </div>

            <div class="field">
                <label class="block text-sm font-medium mb-2">Balance</label>
                <div class="flex gap-4 items-center">
                    <div class="flex-1">
                        <label class="block text-xs text-gray-500 mb-1">Current</label>
                        <div class="p-inputtext w-full bg-gray-50 cursor-not-allowed">
                            {{ formatCurrency(currentBalance) }}
                        </div>
                    </div>
                    <div class="flex-none">→</div>
                    <div class="flex-1">
                        <label class="block text-xs text-gray-500 mb-1">After Transaction</label>
                        <InputNumber 
                            v-model="afterBalance" 
                            mode="currency" 
                            currency="EUR" 
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                            class="w-full" 
                            @input="updateAmountFromAfterBalance"
                            :min="null" 
                        />
                    </div>
                </div>
            </div>

            <div class="field">
                <label for="description" class="block text-sm font-medium mb-2">Description</label>
                <Textarea v-model="newTransaction.description" id="description" rows="3" class="w-full"
                    placeholder="Enter transaction description" />
            </div>
        </div>
        <template #footer>
            <Button label="Cancel" icon="pi pi-times" @click="closeModal" class="p-button-text" severity="secondary" outlined />
            <Button label="Add" icon="pi pi-check" @click="addTransaction" :loading="submitting" severity="primary" />
        </template>
    </Dialog>
</template>

<script setup>
import { ref, computed, watch, onUnmounted} from 'vue';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import WalletService from '@/services/WalletService';
import { formatCurrency } from '@/utils/currencies';

const props = defineProps({
    walletId: {
        type: String,
        required: true
    },
    currentBalance: {
        type: Number,
        required: true
    }
});

const emit = defineEmits(['transaction-added']);

const visible = ref(false);
const submitting = ref(false);

//we store the absolute value of the amount
const amount = ref(0);
const isPositive = ref(false);

const newTransaction = ref({
    amount: 0,
    description: ''
});

const afterBalance = ref(props.currentBalance);

const handleAmountChange = (event) => {
    newTransaction.value.amount = event.value || 0;
};

const calculatedBalance = computed(() => {
    const currentAmount = newTransaction.value.amount || 0;
    return props.currentBalance + (isPositive.value ? currentAmount : -currentAmount);
});

const updateAmountFromAfterBalance = (event) => {
    const difference = (event.value) - props.currentBalance;
    isPositive.value = difference >= 0;
    amount.value = Math.abs(difference);
    newTransaction.value.amount = amount.value;
};

// Update afterBalance when amount changes
watch([() => newTransaction.value.amount, isPositive], () => {
    afterBalance.value = calculatedBalance.value;
});

const toggleModal = () => {
    visible.value = true;
};

const closeModal = () => {
    visible.value = false;
    resetForm();
};

const resetForm = () => {
    amount.value = 0;
    afterBalance.value = props.currentBalance;
    newTransaction.value = {
        amount: 0,
        description: ''
    };
    isPositive.value = false;
};


//api call
const addTransaction = async () => {
    if (!newTransaction.value.amount) {
        // TODO: Add proper validation feedback
        console.error('Please enter an amount', console.log(newTransaction.value));
        return;
    }

    submitting.value = true;
    try {
        const change = isPositive.value
            ? newTransaction.value.amount
            : -newTransaction.value.amount;

        const response = await WalletService.addTransaction(
            props.walletId, 
            change,
            newTransaction.value.description
        );

        emit('transaction-added', response.balance);
        closeModal();
    } catch (error) {
        console.error('Failed to add transaction:', error);
        // TODO: Add error handling feedback
    } finally {
        submitting.value = false;
    }
};

onUnmounted(() => {
    resetForm();
});

</script>